import { z } from 'zod';
import { PRIORITIES, type Priority, type TaskStatus, type UserRef } from './tasks.js';

/**
 * Регулярные задачи (автозадачи): поручение, которое система создаёт сама по расписанию
 * («каждый понедельник», «1-го числа месяца», «в последний рабочий день квартала»).
 * Даты расписания — местные даты организации в виде «ГГГГ-ММ-ДД».
 */

// ---- Расписание ----

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Укажите дату');
const time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Время в виде ЧЧ:ММ');
/** 1 — понедельник … 7 — воскресенье. */
const weekday = z.number().int().min(1).max(7);

/** День месяца: число, последний день, «N-й день недели», первый или последний рабочий день. */
export const daySpecSchema = z.discriminatedUnion('kind', [
  /** Число месяца; в коротком месяце 31-е — последний день. */
  z.object({ kind: z.literal('date'), day: z.number().int().min(1).max(31) }),
  z.object({ kind: z.literal('last') }),
  /** nth: 1–4 или -1 (последний). */
  z.object({ kind: z.literal('weekday'), nth: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(-1)]), weekday }),
  z.object({ kind: z.literal('first_workday') }),
  z.object({ kind: z.literal('last_workday') }),
]);
export type DaySpec = z.infer<typeof daySpecSchema>;

export const WEEKEND_RULES = ['none', 'before', 'after'] as const;
export type WeekendRule = (typeof WEEKEND_RULES)[number];
export const WEEKEND_RULE_LABELS: Record<WeekendRule, string> = {
  none: 'Запускать как есть',
  before: 'Перенести на предыдущий рабочий день',
  after: 'Перенести на следующий рабочий день',
};

const common = {
  /** Во сколько создавать задачу (время организации). */
  time: time.default('09:00'),
  startsOn: isoDate,
  endsOn: isoDate.nullable().default(null),
  /** Остановиться после стольких запусков. */
  maxRuns: z.number().int().min(1).max(10_000).nullable().default(null),
  /** Если день запуска — суббота или воскресенье. */
  weekendRule: z.enum(WEEKEND_RULES).default('none'),
};

export const SCHEDULE_TYPES = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'] as const;
export type ScheduleType = (typeof SCHEDULE_TYPES)[number];
export const SCHEDULE_TYPE_LABELS: Record<ScheduleType, string> = {
  daily: 'Ежедневно',
  weekly: 'Еженедельно',
  monthly: 'Ежемесячно',
  quarterly: 'Ежеквартально',
  yearly: 'Ежегодно',
};

export const scheduleSchema = z
  .discriminatedUnion('type', [
    z.object({ type: z.literal('daily'), every: z.number().int().min(1).max(365).default(1), workdaysOnly: z.boolean().default(false), ...common }),
    z.object({
      type: z.literal('weekly'),
      every: z.number().int().min(1).max(52).default(1),
      weekdays: z.array(weekday).min(1, 'Выберите хотя бы один день недели').max(7),
      ...common,
    }),
    z.object({ type: z.literal('monthly'), every: z.number().int().min(1).max(12).default(1), day: daySpecSchema, ...common }),
    /** Месяц квартала (1–3) и день в нём. */
    z.object({ type: z.literal('quarterly'), month: z.number().int().min(1).max(3), day: daySpecSchema, ...common }),
    z.object({ type: z.literal('yearly'), month: z.number().int().min(1).max(12), day: z.number().int().min(1).max(31), ...common }),
  ])
  .refine((s) => !s.endsOn || s.endsOn >= s.startsOn, { message: 'Дата окончания раньше начала', path: ['endsOn'] });
export type Schedule = z.output<typeof scheduleSchema>;
export type ScheduleInput = z.input<typeof scheduleSchema>;

// ---- Даты ----

const parse = (d: string) => {
  const [y, m, day] = d.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, day);
};
const fmt = (ms: number) => new Date(ms).toISOString().slice(0, 10);
const DAY = 86_400_000;

export function addDays(date: string, n: number): string {
  return fmt(parse(date) + n * DAY);
}
/** 1 — понедельник … 7 — воскресенье. */
export function isoWeekday(date: string): number {
  return ((new Date(parse(date)).getUTCDay() + 6) % 7) + 1;
}
export const isWorkday = (date: string) => isoWeekday(date) <= 5;
const daysIn = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();
const parts = (date: string) => date.split('-').map(Number) as [number, number, number];

/** Суббота или воскресенье → ближайший рабочий день до или после. */
export function shiftWeekend(date: string, rule: WeekendRule): string {
  if (rule === 'none') return date;
  let d = date;
  while (!isWorkday(d)) d = addDays(d, rule === 'before' ? -1 : 1);
  return d;
}

function firstWorkday(y: number, m: number): number {
  for (let d = 1; ; d++) if (isWorkday(fmt(Date.UTC(y, m - 1, d)))) return d;
}
function lastWorkday(y: number, m: number): number {
  for (let d = daysIn(y, m); ; d--) if (isWorkday(fmt(Date.UTC(y, m - 1, d)))) return d;
}

/** Подходит ли число месяца под правило дня. */
function dayMatches(spec: DaySpec, date: string): boolean {
  const [y, m, d] = parts(date);
  const last = daysIn(y, m);
  switch (spec.kind) {
    case 'date':
      return d === Math.min(spec.day, last);
    case 'last':
      return d === last;
    case 'weekday':
      return isoWeekday(date) === spec.weekday && (spec.nth === -1 ? d + 7 > last : Math.ceil(d / 7) === spec.nth);
    case 'first_workday':
      return d === firstWorkday(y, m);
    case 'last_workday':
      return d === lastWorkday(y, m);
  }
}

/** Попадает ли дата в расписание (без учёта начала, окончания и сдвига выходных). */
function matches(s: Schedule, date: string): boolean {
  const [y, m, d] = parts(date);
  const [sy, sm] = parts(s.startsOn);
  switch (s.type) {
    case 'daily':
      return Math.round((parse(date) - parse(s.startsOn)) / DAY) % s.every === 0 && (!s.workdaysOnly || isWorkday(date));
    case 'weekly': {
      const monday = addDays(s.startsOn, 1 - isoWeekday(s.startsOn));
      const weeks = Math.floor(Math.round((parse(date) - parse(monday)) / DAY) / 7);
      return s.weekdays.includes(isoWeekday(date)) && weeks % s.every === 0;
    }
    case 'monthly':
      return ((y - sy) * 12 + (m - sm)) % s.every === 0 && dayMatches(s.day, date);
    case 'quarterly':
      return (m - 1) % 3 === s.month - 1 && dayMatches(s.day, date);
    case 'yearly':
      return m === s.month && d === Math.min(s.day, daysIn(y, m));
  }
}

export interface Occurrence {
  /** Плановая дата периода — по ней один запуск на период. */
  period: string;
  /** Дата запуска после сдвига с выходных. */
  runDate: string;
}

/** Самое длинное расстояние между запусками — год; ищем с запасом. */
const HORIZON_DAYS = 6 * 366;

/** Следующий период строго после after (или с начала расписания, если after нет). */
export function nextOccurrence(s: Schedule, after: string | null): Occurrence | null {
  let d = after && after >= s.startsOn ? addDays(after, 1) : s.startsOn;
  for (let i = 0; i < HORIZON_DAYS; i++, d = addDays(d, 1)) {
    if (s.endsOn && d > s.endsOn) return null;
    if (matches(s, d)) return { period: d, runDate: shiftWeekend(d, s.weekendRule) };
  }
  return null;
}

/** Ближайшие n запусков начиная с from (включительно) — для предпросмотра в настройке. */
export function upcomingOccurrences(s: Schedule, from: string, n: number, maxRuns = s.maxRuns): Occurrence[] {
  const out: Occurrence[] = [];
  let after: string | null = from > s.startsOn ? addDays(from, -1) : null;
  while (out.length < n && (maxRuns === null || out.length < maxRuns)) {
    const next = nextOccurrence(s, after);
    if (!next) break;
    out.push(next);
    after = next.period;
  }
  return out;
}

/** Местное время организации → момент времени (без библиотек, через Intl). */
export function localToUtc(date: string, hhmm: string, tz: string): Date {
  const [y, m, d] = parts(date);
  const [h, mi] = hhmm.split(':').map(Number) as [number, number];
  const wanted = Date.UTC(y, m - 1, d, h, mi);
  const wall = (ms: number) => {
    const p = Object.fromEntries(
      new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
        .formatToParts(new Date(ms))
        .map((x) => [x.type, x.value]),
    );
    return Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute));
  };
  let guess = wanted - (wall(wanted) - wanted);
  guess = wanted - (wall(guess) - guess);
  return new Date(guess);
}

// ---- Подписи ----

const MONTHS = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];
const MONTHS_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
export const WEEKDAY_SHORT = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];
const WEEKDAY_DAT = ['понедельникам', 'вторникам', 'средам', 'четвергам', 'пятницам', 'субботам', 'воскресеньям'];
const WEEKDAY_ACC = ['понедельник', 'вторник', 'среду', 'четверг', 'пятницу', 'субботу', 'воскресенье'];
/** Род дня недели: м, ж, ср — для «первый понедельник», «первую среду», «первое воскресенье». */
const WEEKDAY_GENDER = [0, 0, 1, 0, 1, 1, 2];
const NTH: Record<string, [string, string, string]> = {
  '1': ['первый', 'первую', 'первое'],
  '2': ['второй', 'вторую', 'второе'],
  '3': ['третий', 'третью', 'третье'],
  '4': ['четвёртый', 'четвёртую', 'четвёртое'],
  '-1': ['последний', 'последнюю', 'последнее'],
};
const ROMAN = ['I', 'II', 'III', 'IV'];

export const dmy = (date: string) => date.split('-').reverse().join('.');

function joinRu(items: string[]): string {
  return items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} и ${items.at(-1)}`;
}

export function daySpecText(spec: DaySpec): string {
  switch (spec.kind) {
    case 'date':
      return `${spec.day}-го числа`;
    case 'last':
      return 'в последний день';
    case 'weekday':
      return `в ${NTH[String(spec.nth)]![WEEKDAY_GENDER[spec.weekday - 1]!]} ${WEEKDAY_ACC[spec.weekday - 1]}`;
    case 'first_workday':
      return 'в первый рабочий день';
    case 'last_workday':
      return 'в последний рабочий день';
  }
}

/** Расписание словами: «Каждый месяц, 1-го числа, в 09:00». */
export function describeSchedule(s: Schedule): string {
  let what: string;
  switch (s.type) {
    case 'daily':
      what = s.every === 1 ? (s.workdaysOnly ? 'Каждый рабочий день' : 'Каждый день') : `Раз в ${s.every} дн.${s.workdaysOnly ? ', только в рабочие дни' : ''}`;
      break;
    case 'weekly': {
      const days = joinRu([...s.weekdays].sort().map((w) => WEEKDAY_DAT[w - 1]!));
      what = `${s.every === 1 ? 'Каждую неделю' : `Раз в ${s.every} нед.`} по ${days}`;
      break;
    }
    case 'monthly':
      what = `${s.every === 1 ? 'Каждый месяц' : `Раз в ${s.every} мес.`}, ${daySpecText(s.day)}`;
      break;
    case 'quarterly': {
      // Первый рабочий день первого месяца и последний день третьего — это начало и конец квартала.
      const edge = (s.month === 1 && s.day.kind === 'first_workday') || (s.month === 3 && (s.day.kind === 'last' || s.day.kind === 'last_workday'));
      what = `Каждый квартал, ${daySpecText(s.day)} ${edge ? 'квартала' : `${['первого', 'второго', 'третьего'][s.month - 1]} месяца`}`;
      break;
    }
    case 'yearly':
      what = `Каждый год ${s.day} ${MONTHS_GEN[s.month - 1]}`;
      break;
  }
  const extra = [
    `в ${s.time}`,
    s.weekendRule === 'before' ? 'с выходных — на предыдущий рабочий день' : s.weekendRule === 'after' ? 'с выходных — на следующий рабочий день' : null,
    `с ${dmy(s.startsOn)}`,
    s.endsOn ? `по ${dmy(s.endsOn)}` : null,
    s.maxRuns ? `не более ${s.maxRuns} раз` : null,
  ].filter(Boolean);
  return `${what}, ${extra.join(', ')}`;
}

/** Период запуска для названия: «октябрь 2026», «III квартал 2026», «неделя 12.10–18.10.2026». */
export function periodLabel(s: Pick<Schedule, 'type'>, period: string): string {
  const [y, m] = parts(period);
  switch (s.type) {
    case 'daily':
      return dmy(period);
    case 'weekly': {
      const monday = addDays(period, 1 - isoWeekday(period));
      const sunday = addDays(monday, 6);
      return `неделя ${dmy(monday).slice(0, 5)}–${dmy(sunday)}`;
    }
    case 'monthly':
      return `${MONTHS[m - 1]} ${y}`;
    case 'quarterly':
      return `${ROMAN[Math.floor((m - 1) / 3)]} квартал ${y}`;
    case 'yearly':
      return `${y} год`;
  }
}

/** Название запуска по шаблону: {{name}}, {{period}}, {{date}} (дата запуска). */
export function runTitle(template: string, name: string, s: Pick<Schedule, 'type'>, o: Occurrence): string {
  const t = template.trim() || '{{name}} — {{period}}';
  return t
    .replaceAll('{{name}}', name)
    .replaceAll('{{period}}', periodLabel(s, o.period))
    .replaceAll('{{date}}', dmy(o.runDate))
    .slice(0, 500);
}

// ---- Регулярная задача ----

export const REGULATION_MODES = ['parallel', 'sequential'] as const;
export type RegulationMode = (typeof REGULATION_MODES)[number];
export const REGULATION_MODE_LABELS: Record<RegulationMode, string> = {
  parallel: 'Все одновременно',
  sequential: 'По очереди',
};

export const regulationInputSchema = z.object({
  name: z.string().trim().min(1, 'Укажите название').max(200),
  /** Название каждой задачи; пусто — «{{name}} — {{period}}». */
  titleTemplate: z.string().trim().max(300).default(''),
  description: z.string().trim().max(20_000).nullable().default(null),
  priority: z.enum(PRIORITIES).default('normal'),
  responsibleId: z.uuid('Выберите ответственного'),
  executors: z
    .array(z.object({ userId: z.uuid('Выберите исполнителя'), instruction: z.string().trim().max(2000).nullable().default(null) }))
    .min(1, 'Добавьте хотя бы одного исполнителя')
    .max(50)
    .refine((list) => new Set(list.map((e) => e.userId)).size === list.length, 'Исполнитель указан дважды'),
  mode: z.enum(REGULATION_MODES).default('parallel'),
  /** Срок исполнителям: через сколько дней после запуска; null — без срока. */
  dueDays: z.number().int().min(0).max(365).nullable().default(null),
  dueTime: time.default('18:00'),
  schedule: scheduleSchema,
  isActive: z.boolean().default(true),
});
export type RegulationInput = z.input<typeof regulationInputSchema>;

/** Срок исполнителя для запуска: дата запуска + N дней, с выходных — по правилу расписания. */
export function runDueDate(r: { dueDays: number | null; schedule: Pick<Schedule, 'weekendRule'> }, runDate: string): string | null {
  return r.dueDays === null ? null : shiftWeekend(addDays(runDate, r.dueDays), r.schedule.weekendRule);
}

export interface RegulationRunDto {
  taskId: string;
  number: number;
  title: string;
  period: string;
  status: TaskStatus;
  createdAt: string;
  dueAt: string | null;
  /** Принято исполнителей из всех. */
  accepted: number;
  total: number;
}

export interface RegulationDto {
  id: string;
  name: string;
  titleTemplate: string;
  description: string | null;
  priority: Priority;
  responsible: UserRef;
  executors: Array<{ user: UserRef; instruction: string | null }>;
  mode: RegulationMode;
  dueDays: number | null;
  dueTime: string;
  schedule: Schedule;
  isActive: boolean;
  nextRunAt: string | null;
  nextPeriod: string | null;
  runsCount: number;
  createdAt: string;
  canEdit: boolean;
  runs?: RegulationRunDto[];
}
